import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CandidateList from './pages/CandidateList';
import CandidateDetail from './pages/CandidateDetail';
import CreateCandidate from './pages/CreateCandidate';
import Help from './pages/Help';
import Users from './pages/Users';
import ForcePasswordChange from './pages/ForcePasswordChange';
import FeedbackModal from './components/FeedbackModal';
import api, { authAPI } from './services/api';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // null = unknown (not yet checked); true/false once /me has resolved.
  const [mustChangePassword, setMustChangePassword] = useState(null);
  // The authenticated account, so the forced-change screen can name it.
  const [authUser, setAuthUser] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  // Whenever we're authenticated, ask the server whether this account is still
  // on a temporary password. If so, the whole app is gated behind the
  // mandatory password-change screen below.
  useEffect(() => {
    if (!isAuthenticated) {
      setMustChangePassword(null);
      setAuthUser(null);
      return;
    }
    let cancelled = false;
    authAPI
      .me()
      .then((user) => {
        if (cancelled) return;
        setAuthUser(user);
        setMustChangePassword(!!user.must_change_password);
      })
      .catch(() => {
        // 401 is handled by the axios interceptor (redirects to login). For any
        // other error, don't lock the user out of the app.
        if (!cancelled) setMustChangePassword(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleLogin = (token) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
  };

  const handleFeedbackOpen = () => {
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = async (feedbackText) => {
    const context = {
      url: window.location.href,
      pathname: window.location.pathname,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      screenResolution: `${window.screen.width}x${window.screen.height}`
    };

    await api.post('/api/feedback', {
      feedback: feedbackText,
      context: context
    });
  };

  // Mandatory first-login password change: block the entire app until the
  // temporary password has been replaced.
  if (isAuthenticated && mustChangePassword) {
    return (
      <div className="App">
        <ForcePasswordChange
          user={authUser}
          onDone={() => setMustChangePassword(false)}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                <Dashboard onLogout={handleLogout} onFeedback={handleFeedbackOpen} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/candidates"
            element={
              isAuthenticated ? (
                <CandidateList onLogout={handleLogout} onFeedback={handleFeedbackOpen} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/candidates/new"
            element={
              isAuthenticated ? (
                <CreateCandidate onLogout={handleLogout} onFeedback={handleFeedbackOpen} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/candidates/:id"
            element={
              isAuthenticated ? (
                <CandidateDetail onLogout={handleLogout} onFeedback={handleFeedbackOpen} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/help"
            element={
              isAuthenticated ? (
                <Help onLogout={handleLogout} onFeedback={handleFeedbackOpen} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/users"
            element={
              !isAuthenticated ? (
                <Navigate to="/login" replace />
              ) : !authUser ? null : /* /me still loading */ authUser.is_admin ? (
                <Users onLogout={handleLogout} onFeedback={handleFeedbackOpen} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>

        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
        />
      </div>
    </Router>
  );
}

export default App;
